'use client'

import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import {
  AdPage,
  AdShape,
  visibleDurationEvent,
  ClickAdEventTarget,
} from '@marketplace-web/ads/ads-data'
import { ConsentGroup, useIsConsentGroupEnabled } from '@marketplace-web/consent/consent-feature'
import { useUserAgent } from '@marketplace-web/environment/request-context-data'
import { useSystemConfiguration } from '@marketplace-web/shared/system-configuration-data'
import { CountryCode } from '@marketplace-web/country/country-data'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'

import VanMultiOfferContent from './VanMultiOfferContent/VanMultiOfferContent'
import AdsContext from '../../../containers/AdsProvider/AdsContext'
import { useShouldShowRokt } from '../RoktAdvertisement'
import useShouldShowVanOffer from '../../../hooks/useShouldShowVanOffer'
import useFetchVanMultiPlacements from '../../../hooks/useFetchVanMultiPlacements'
import useVisibleTimeTracker from '../../../hooks/useVisibleTimeTracker'
import useClickAdEventTracking from '../../../hooks/useClickAdEventTracking'

const placementId = 'web_purchase_van'

const VanMultiOfferAdvertisement = () => {
  const [isOpen, setIsOpen] = useState(true)
  const [currentPlacementIndex, setCurrentPlacementIndex] = useState(0)
  const hasConsentedTargeting = useIsConsentGroupEnabled(ConsentGroup.VanAdvertising)
  const { transaction, conversation } = useConversationContext()
  const isDebitProcessed = transaction?.isDebitProcessed
  const purchaseId = conversation?.transaction?.purchaseId

  const userAgent = useUserAgent()
  const { getAdsPlatform } = useContext(AdsContext)
  const { userCountry: countryCode } = useSystemConfiguration()

  const { shouldShowVanOffer, setShouldShowVan } = useShouldShowVanOffer()
  const { setShouldShowRokt, hasRoktBeenShown } = useShouldShowRokt()

  const { startTimingVisible, stopAndGetVisibleSeconds } = useVisibleTimeTracker()
  const { trackClickAdEvent } = useClickAdEventTracking()
  const isImpressionTrackingEnabled = useFeatureSwitch('web_van_offer_duration_tracking')
  const hasTrackedRef = useRef(false)
  const { track } = useTracking()

  const { placements: fetchedPlacements, fetchVanMultiAdPlacements } = useFetchVanMultiPlacements({
    placementId,
    shape: AdShape.Rectangle,
    consentStatus: hasConsentedTargeting,
    page: AdPage.Messages,
    platform: getAdsPlatform(userAgent),
    countryCode: countryCode as CountryCode,
  })

  const handleTrackAndClose = useCallback(() => {
    if (!isOpen || hasTrackedRef.current) return

    hasTrackedRef.current = true

    if (!isImpressionTrackingEnabled) return

    const currentPlacement = fetchedPlacements[currentPlacementIndex]
    if (!currentPlacement?.correlationId || !currentPlacement.id) return

    const visibleSeconds = stopAndGetVisibleSeconds()
    track(
      visibleDurationEvent({
        campaignId: currentPlacement.id,
        correlationId: currentPlacement.correlationId,
        duration: visibleSeconds,
      }),
    )
  }, [
    isOpen,
    fetchedPlacements,
    currentPlacementIndex,
    isImpressionTrackingEnabled,
    stopAndGetVisibleSeconds,
    track,
  ])

  const onClose = (target: ClickAdEventTarget) => {
    const placement = fetchedPlacements[0]

    if (placement) {
      trackClickAdEvent(placement, target)
    }
    handleTrackAndClose()

    setIsOpen(false)
  }

  const shouldRunVanAd = shouldShowVanOffer && isDebitProcessed && !hasRoktBeenShown

  useEffect(() => {
    if (!shouldRunVanAd) return

    fetchVanMultiAdPlacements(purchaseId)
    setShouldShowVan(false)
  }, [fetchVanMultiAdPlacements, setShouldShowVan, shouldRunVanAd, purchaseId])

  useEffect(() => {
    if (!shouldShowVanOffer) return

    setShouldShowRokt(false)
  }, [shouldShowVanOffer, setShouldShowRokt])

  useEffect(() => {
    startTimingVisible()
  }, [fetchedPlacements, startTimingVisible])

  useEffect(() => {
    window.addEventListener('beforeunload', handleTrackAndClose)

    return () => {
      window.removeEventListener('beforeunload', handleTrackAndClose)
    }
  }, [handleTrackAndClose])

  if (fetchedPlacements.length === 0) return null

  return (
    <div data-testid="van-multi-offer-content">
      <VanMultiOfferContent
        isOpen={isOpen}
        onClose={onClose}
        placements={fetchedPlacements}
        currentPlacementIndex={currentPlacementIndex}
        onPlacementIndexChange={setCurrentPlacementIndex}
      />
    </div>
  )
}

export default VanMultiOfferAdvertisement
